/**
 * The pencil loop around the pressed filter button. Adapted from
 * https://codepen.io/Veng1/pen/QWKaRON (jQuery, on :hover) -- the geometry is
 * that pen's, the plumbing is not.
 *
 * FOUR BEZIERS THAT DO NOT QUITE MEET. A circle drawn as four cubic segments
 * needs the control-point constant 0.5519; wobbling the radius and over-
 * turning each quarter is what makes it read as a pencil rather than a shape.
 * The ends run past each other, and that overlap is the whole tell.
 *
 * THE SHAPE IS NOT MEASURED, THE LINE LENGTH IS. Those are different problems
 * and the pen answered both with one init-time measurement, which is why it
 * was wrong after a webfont swap and wrong for any label but the one it was
 * tuned on. Here a square viewBox stretched with preserveAspectRatio="none"
 * fits the loop to the button at any size with no script at all. Only the
 * DASH needs a number -- see paintedLength() -- and it is taken at the moment
 * of drawing, so it is current by construction and needs no listener.
 *
 * UNSEEDED: a fresh lap on every draw. A seeded mode was built and compared,
 * so that each filter would keep one lap for the life of the site; Ben looked
 * at both and switched it off, 2026-09-22. The fifteen lines that did it are
 * not kept here unused -- a branch nothing reaches is not a decision record,
 * it is a rule matching nothing. The comparison is in the design document.
 */

const NS = 'http://www.w3.org/2000/svg'

/* Tuned against the longest label, not the shortest. Rasterised 30 laps over
   each of the eight real labels in both language trees and counted the pixels
   where the line lands on a glyph: 8/8 clean, 30/30. The pen's own numbers
   score 6 clean out of 75 on the same test -- it was tuned on four short
   words, and SCHOKOLADIG is not a short word.

   What governs the result is the ratio of the glyph run to the padded box;
   below about 0.80 the lap stays off the letters. The real labels run
   0.57-0.73 at the filter's 24px -- see --font-size-filter in base/_tokens,
   which is flat at every width for this reason. Approved by Ben 2026-09-22. */
export const SHAPE = {
  radius: 1.00,
  floor: 0.92,                    // see the clamp below -- how far in the lap may come
  drMin: -0.31, drMax: 0.11,      // radius drift per quarter
  sweepMin: 0.04, sweepMax: 0.20, // extra turn per quarter -- the overlap
  angleMin: 155, angleMax: 200    // touch-down, degrees clockwise from the top
}

/* One lap, drawn as the pen drew it. Nine numbers are drawn: one start angle,
   then a sweep and a drift for each of the four quarters. */
function circlePath (o) {
  const rand = (min, max) => min + Math.random() * (max - min)
  const c = 0.551915024494          // circle-from-beziers constant
  const beta = Math.atan(c)
  const reach = Math.hypot(c, 1)    // control-point distance, as a fraction of r

  let r = o.radius
  let angle = rand(o.angleMin, o.angleMax) * Math.PI / 180

  /* THE DRIFT COMPOUNDS, WHICH IS WHY IT NEEDS A FLOOR. Each quarter
     multiplies the radius, so drMin alone is not the worst case: four bad
     draws in a row give 0.9 * 0.85^4 = 0.47, a lap at less than half the box
     that runs straight through the letters. The pen had no limit and got away
     with it on four short words.

     Clamping the low side only. A lap that drifts OUTWARD is fine -- it leaves
     the padding box, nothing clips, and it reads as a generous sweep. */
  const inner = o.radius * o.floor

  const at = (a, k = 1) =>
    `${(k * r * Math.sin(a)).toFixed(4)},${(k * r * Math.cos(a)).toFixed(4)}`

  let path = `M${at(angle)} C${at(angle + beta, reach)}`
  for (let i = 0; i < 4; i++) {
    angle += Math.PI / 2 * (1 + rand(o.sweepMin, o.sweepMax))
    r = Math.max(r * (1 + rand(o.drMin, o.drMax)), inner)
    path += ` ${i ? 'S' : ''}${at(angle - beta, reach)} ${at(angle)}`
  }
  return path
}

/**
 * How long the line is once it has been PAINTED, in CSS pixels.
 *
 * This is the one number CSS cannot supply and the reason the module measures
 * at all. getTotalLength() answers in the viewBox's own units -- about 5.6 --
 * but preserveAspectRatio="none" stretches the two axes by different factors
 * and vector-effect then strokes the result in screen space, so neither the
 * user-space length nor pathLength describes what is actually drawn.
 *
 * Setting pathLength="100" and dashing against that is the obvious-looking
 * answer and it is wrong: the two features compose in opposite spaces, the
 * dash comes out as a repeating pattern instead of one lap, and the offset
 * that should hide the loop only shifts the pattern -- the line never erases.
 * Measured rather than argued, 2026-09-22.
 *
 * 96 samples is well inside a pixel on a loop this smooth.
 */
function paintedLength (path, rect) {
  const sx = rect.width / 2, sy = rect.height / 2   // the viewBox is 2 x 2
  const total = path.getTotalLength()
  let sum = 0
  let prev = path.getPointAtLength(0)
  for (let i = 1; i <= 96; i++) {
    const point = path.getPointAtLength(total * i / 96)
    sum += Math.hypot((point.x - prev.x) * sx, (point.y - prev.y) * sy)
    prev = point
  }
  return sum
}

/* THE SPAN IS WHAT MAKES THE INSETS APPLY. An svg with a viewBox carries an
   intrinsic aspect ratio, and an absolutely positioned replaced element takes
   its size from that ratio rather than from four inset values -- a 92x42
   button was handed a 129x129 svg, and a 236x42 one a 273x273. The span is a
   plain box, so the insets decide; the svg fills it at 100%. */
function ensure (el) {
  let box = el.querySelector(':scope > .scribble')
  if (box) return box

  box = document.createElement('span')
  box.className = 'scribble'
  box.setAttribute('aria-hidden', 'true')

  const svg = document.createElementNS(NS, 'svg')
  svg.setAttribute('viewBox', '-1 -1 2 2')
  /* The square lap is stretched to the button's box. A long label gets a long
     ellipse, which is what a pencil would do; non-scaling-stroke keeps the
     line from being stretched with it. */
  svg.setAttribute('preserveAspectRatio', 'none')
  svg.setAttribute('focusable', 'false')

  const path = document.createElementNS(NS, 'path')
  path.setAttribute('vector-effect', 'non-scaling-stroke')

  svg.append(path)
  box.append(svg)
  el.append(box)
  return box
}

/**
 * Show or hide the loop on `el`. A fresh lap is generated on every show, so
 * the same button never gets the same circle twice.
 *
 * `animate` is false for the state the page LOADS in -- the pressed button and
 * the #schokoladig deep link. Drawing a line the reader did not ask for, at a
 * spot they have not looked at yet, is the same mistake reveal.js avoids by
 * settling a deep-linked section instead of animating it.
 */
export function scribble (el, on, { animate = true, shape = SHAPE } = {}) {
  if (!on) {
    const box = el.querySelector(':scope > .scribble')
    if (box) box.querySelector('path').style.strokeDashoffset = box.dataset.rest
    return
  }

  const box = ensure(el)
  const path = box.querySelector('path')
  path.setAttribute('d', circlePath(shape))

  const rect = box.getBoundingClientRect()
  if (!rect.width || !rect.height) return   // not laid out; nothing to draw on
  const lap = paintedLength(path, rect)

  /* THE GAP IS WIDER THAN THE LAP, AND THAT IS THE POINT. Dashing `lap lap`
     and parking the offset at `lap` leaves a stretch of exactly zero length
     sitting on the path's first point -- and stroke-linecap: round paints a
     disc at a zero-length dash. The line erased and left a 1.6px dot behind,
     at the top of the word where the pen touches down. Reported 2026-09-22.

     Widening the gap by a stroke width at each end, and resting the offset a
     stroke width early, puts both caps off the path instead. Measured: 0px of
     coverage at rest, the full lap at offset 0. Not `linecap: butt`, which
     would fix the dot by taking the rounded pencil ends off the drawn line
     too. */
  const width = parseFloat(getComputedStyle(path).strokeWidth) || 1
  const rest = lap + width
  box.dataset.rest = rest

  /* Reset to hidden with the transition off, commit it, then draw. Without
     the forced reflow the browser coalesces both writes and nothing moves. */
  box.classList.add('is-instant')
  path.style.strokeDasharray = `${lap} ${lap + 2 * width}`
  path.style.strokeDashoffset = rest
  void box.getBoundingClientRect()

  box.classList.toggle('is-instant', !animate)
  path.style.strokeDashoffset = '0'
  if (!animate) {
    void box.getBoundingClientRect()
    box.classList.remove('is-instant')   // so the erase still animates
  }
}
